/**
 * Quelle einer Antwort im Fragenkatalog — die REGELN (Migration Nr. 190).
 *
 * Betreiber: „Wo steht, wer uns Frage XY beantwortet hat?" Drei Felder an
 * der Frage (`antwort_von`, `antwort_weg`, `antwort_beleg_id`, siehe
 * `Planungsfrage` in types.ts). Hier steht, was man ohne DOM, ohne Supabase
 * und ohne KI pruefen kann (`antwortQuelle.test.ts`):
 *
 *   - Wege und ihre Beschriftungen, robustes Lesen eines Wegs,
 *   - die Quellenzeile fuer Liste und PDF (`quelleText`),
 *   - die Vorschlaege fuer „Antwort von" aus Empfaenger, Zuordnung und
 *     Verteiler (`vorschlaegeFuerAntwortVon`) und die ganze Auswahl mit
 *     allen Beteiligten (`auswahlFuerAntwortVon`, `antwortVonAusKontakt`,
 *     `antwortVonAusBauherr`, `antwortVonModus`),
 *   - der Absender einer eingefuegten Mail (`absenderAusMail`) und der Teil
 *     der Mail, der an die KI geht (`mailTextFuerKi`),
 *   - der Eingabestand des Feldes „Antwort von" (`antwortVonAusEingabe`,
 *     `antwortVonGeaendert`).
 *
 * ═══ ZUSAGEN ═════════════════════════════════════════════════════════════
 *  - Der Absender einer Mail wird NUR LOKAL gelesen. An die KI geht NUR der
 *    eigene Text der Mail — ohne den Kopf oben (Von/An/Betreff bzw. die
 *    Gmail-Kopfzeilen, auch mit Betreffzeile davor) und ohne den zitierten
 *    Verlauf unten („Von: … Gesendet: …", „Am … schrieb …:"); jede dann
 *    noch verbliebene Mailadresse wird zu „[Adresse entfernt]"
 *    (`mailTextFuerKi`). Ein NAME in der Signatur bleibt Sache des Nutzers
 *    (der Dialog sagt, dass Signaturen vorher heraus sollen).
 *  - „Antwort von" aus einer Mail ist der Name des Absenders, die Adresse
 *    nur, wenn kein Name erkennbar ist (`antwortVonAusAbsender`).
 *  - Antwortmail (Setzen, Anhaengen) und Ruecklauf setzen Quelle und Weg nur
 *    dort, wo noch nichts steht. Nur ein ausdrueckliches ERSETZEN der Antwort
 *    durch die Mail ersetzt auch die Quelle (`quelleAusMail`).
 */

import { eigenerTextBereich } from '../../services/planungKi/auswertung';
import {
    vorschlagEmpfaenger,
    type EmpfaengerQuellen,
    type EmpfaengerZuordnung,
    type KontaktQuelle,
    type ProjektStammdaten,
} from './empfaenger';
import type { AntwortWeg, FrageStatus, Planungsfrage } from './types';

export type { AntwortWeg } from './types';

// ═══════════════════════════════════════════════════════════════════════════
// WEGE
// ═══════════════════════════════════════════════════════════════════════════

/** Alle Wege in Anzeigereihenfolge — genau die Werte des CHECK in Nr. 190. */
export const ANTWORT_WEGE: readonly AntwortWeg[] = ['email', 'schreiben', 'besprechung', 'telefon', 'unterlage'];

export const ANTWORT_WEG_LABEL: Record<AntwortWeg, string> = {
    email: 'E-Mail',
    schreiben: 'Schreiben',
    besprechung: 'Besprechung/Protokoll',
    telefon: 'Telefon',
    unterlage: 'Unterlage',
};

/** Ein Weg aus der Datenbank oder einem Auswahlfeld — Unbekanntes und Leeres
 *  ist `null` („nicht angegeben"), nie ein Absturz. */
export const antwortWegAus = (v: unknown): AntwortWeg | null =>
    ANTWORT_WEGE.find(w => w === v) ?? null;

// ═══════════════════════════════════════════════════════════════════════════
// QUELLENZEILE
// ═══════════════════════════════════════════════════════════════════════════

/** Hoechstlaenge von `antwort_von` — CHECK `planungsfragen_antwort_von_check` (Nr. 190). */
export const ANTWORT_VON_MAX = 300;

export const QUELLE_NICHT_ERFASST = 'Quelle nicht erfasst';
/** Beleg verknuepft, Titel aber nicht bekannt (der Aufrufer liefert ihn nicht). */
export const BELEG_OHNE_TITEL = 'Beleg in der Ablage';

/** Nur bei diesen Staenden gibt es eine Antwort, deren Quelle zaehlt. */
const MIT_QUELLE: readonly FrageStatus[] = ['beantwortet', 'teilbeantwortet'];

/** Mehrzeiliges zu einer Zeile, Rand-Leerraum weg. */
const einzeilig = (v: string | null | undefined): string =>
    String(v ?? '').replace(/\s+/g, ' ').trim();

/** ISO-Tag bzw. Zeitstempel → TT.MM.JJJJ (dieselbe Lesart wie `datumDeutsch`
 *  in unterlagenLogic.ts, damit Liste und Dokumentation dasselbe Datum zeigen). */
const tagDeutsch = (iso: string | null | undefined): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

export type QuellenFrage = Pick<Planungsfrage, 'status'>
    & Partial<Pick<Planungsfrage, 'beantwortet_am' | 'antwort_von' | 'antwort_weg' | 'antwort_beleg_id'>>;

/**
 * Die Quellenzeile einer Antwort: „Frau Keller, Stadt Lindenhain · E-Mail ·
 * Eingang 26.09.2026". Ohne jede Angabe „Quelle nicht erfasst · Eingang …".
 * Ein Beleg steht als „Beleg: <Titel>", ohne Titel als „Beleg in der
 * Ablage". Nur bei beantwortet/teilbeantwortet — sonst ''.
 */
export const quelleText = (frage: QuellenFrage | null | undefined, belegTitel?: string | null): string => {
    if (!frage || !MIT_QUELLE.includes(frage.status)) return '';
    const teile: string[] = [];
    const von = einzeilig(frage.antwort_von);
    if (von) teile.push(von);
    const weg = antwortWegAus(frage.antwort_weg);
    if (weg) teile.push(ANTWORT_WEG_LABEL[weg]);
    if (einzeilig(frage.antwort_beleg_id)) {
        const titel = einzeilig(belegTitel);
        teile.push(titel ? `Beleg: ${titel}` : BELEG_OHNE_TITEL);
    }
    if (!teile.length) teile.push(QUELLE_NICHT_ERFASST);
    const eingang = tagDeutsch(frage.beantwortet_am);
    if (eingang) teile.push(`Eingang ${eingang}`);
    return teile.join(' · ');
};

// ═══════════════════════════════════════════════════════════════════════════
// VORSCHLAEGE FUER „ANTWORT VON"
// ═══════════════════════════════════════════════════════════════════════════

export interface AntwortVonQuellen {
    /** Zuordnung Adressat → Person der Planungsphase (`planungsphasen.empfaenger`). */
    zuordnung?: EmpfaengerZuordnung | null;
    /** Projektstammdaten und Verteiler — dieselben Quellen wie „Empfänger je Adressat". */
    quellen?: EmpfaengerQuellen | null;
}

/**
 * Ein Wert der Auswahl „Antwort von", in EINER Schreibweise fuer beide
 * Gruppen: eine Zeile, „Name (Name)" als „Name" (ohne Gross/klein) und auf
 * die Hoechstlaenge des CHECK gekuerzt.
 *
 * Die Angleichung, weil `kontaktName` und die Stammdaten in
 * `vorschlagEmpfaenger` die Firma auch dann in Klammern setzen, wenn sie dem
 * Namen gleicht — `antwortVonAusKontakt` tut das nicht; ohne Angleichung
 * stuende dieselbe Person in beiden Gruppen verschieden geschrieben. Gekuerzt
 * wie `antwortVonAusEingabe`, weil ein gewaehlter Eintrag genau so
 * gespeichert wird und danach wieder als Eintrag erkannt werden muss.
 */
const auswahlWert = (v: string | null | undefined): string => {
    const text = einzeilig(v);
    const klammer = text.match(/^(.+) \((.+)\)$/);
    const ohneDoppel = klammer && klammer[1].trim().toLowerCase() === klammer[2].trim().toLowerCase()
        ? klammer[1].trim()
        : text;
    return ohneDoppel.slice(0, ANTWORT_VON_MAX).trim();
};

/** „Name (Firma)", wenn beides da ist und sich unterscheidet (ohne
 *  Gross/klein) — sonst, was da ist. */
const nameUndFirma = (name: string | null | undefined, firma: string | null | undefined): string => {
    const n = einzeilig(name);
    const f = einzeilig(firma);
    if (n && f && n.toLowerCase() !== f.toLowerCase()) return `${n} (${f})`;
    return n || f;
};

/** „Antwort von" fuer einen Kontakt des Projektverteilers: „Name (Firma)",
 *  wenn beides da und verschieden ist, sonst was da ist; '' ohne beides.
 *  Dieselbe Schreibweise wie die Vorschlaege aus `vorschlagEmpfaenger`
 *  (nach `auswahlWert`), hoechstens `ANTWORT_VON_MAX` Zeichen. */
export const antwortVonAusKontakt = (k: Pick<KontaktQuelle, 'name' | 'company'> | null | undefined): string =>
    (k ? auswahlWert(nameUndFirma(k.name, k.company)) : '');

/** „Antwort von" fuer den Bauherrn laut Projektstammdaten: „Ansprechpartner
 *  (Bauherr)" wie in `vorschlagEmpfaenger`, sonst was da ist; '' ohne beides. */
export const antwortVonAusBauherr = (s: ProjektStammdaten | null | undefined): string =>
    (s ? auswahlWert(nameUndFirma(s.clientContactPerson, s.clientName)) : '');

/**
 * Namen fuer die Auswahlliste unter „Antwort von": zuerst der Empfaenger der
 * Frage (`empfaenger_name`, an ihn ging sie), dann je Adressat der Frage die
 * zugeordnete Person, dann die Vorschlaege aus Stammdaten und Verteiler
 * (`vorschlagEmpfaenger`). Jeder Name einmal (ohne Gross/klein), Leeres
 * faellt weg, Schreibweise nach `auswahlWert`. Nur Vorschlaege — Freitext
 * bleibt immer moeglich.
 */
export const vorschlaegeFuerAntwortVon = (
    frage: Pick<Planungsfrage, 'adressat'> & Partial<Pick<Planungsfrage, 'empfaenger_name'>>,
    quellen?: AntwortVonQuellen | null,
): string[] => {
    const out: string[] = [];
    const nimm = (name: string | null | undefined) => {
        const n = auswahlWert(name);
        if (!n) return;
        const klein = n.toLowerCase();
        if (out.some(o => o.toLowerCase() === klein)) return;
        out.push(n);
    };
    nimm(frage.empfaenger_name);
    const adressaten = frage.adressat || [];
    adressaten.forEach(a => nimm(quellen?.zuordnung?.[a]?.name));
    adressaten.forEach(a => vorschlagEmpfaenger(a, quellen?.quellen).forEach(v => nimm(v.eintrag.name)));
    return out;
};

/** Die Auswahl unter „Antwort von" in zwei Gruppen. */
export interface AntwortVonAuswahl {
    /** „Passend zur Frage" — `vorschlaegeFuerAntwortVon`, in dieser Reihenfolge. */
    passend: string[];
    /** „Weitere Beteiligte" — Bauherr laut Stammdaten und ALLE Kontakte des
     *  Verteilers, die nicht schon passend stehen; alphabetisch. */
    weitere: string[];
}

/** Deutsche Sortierung: Umlaute bei ihrem Grundbuchstaben, Gross/klein egal, „2" vor „10". */
const SORTIERUNG = new Intl.Collator('de', { sensitivity: 'base', numeric: true });

/**
 * Alles, was unter „Antwort von" zur Wahl steht: oben die zur Frage
 * passenden Namen (`vorschlaegeFuerAntwortVon`), darunter jeder weitere
 * Beteiligte des Projekts — der Bauherr aus den Stammdaten
 * (`antwortVonAusBauherr`) und jeder Kontakt des Verteilers
 * (`antwortVonAusKontakt`). Jeder Name steht nur einmal ueber BEIDE Gruppen
 * (ohne Gross/klein), Leeres faellt weg, die weiteren sind sortiert. Freie
 * Angabe bleibt immer moeglich — das ist Sache des Feldes.
 */
export const auswahlFuerAntwortVon = (
    frage: Pick<Planungsfrage, 'adressat'> & Partial<Pick<Planungsfrage, 'empfaenger_name'>>,
    quellen?: AntwortVonQuellen | null,
): AntwortVonAuswahl => {
    const passend = vorschlaegeFuerAntwortVon(frage, quellen);
    const gesehen = new Set(passend.map(p => p.toLowerCase()));
    const weitere: string[] = [];
    const nimm = (name: string) => {
        const klein = name.toLowerCase();
        if (!name || gesehen.has(klein)) return;
        gesehen.add(klein);
        weitere.push(name);
    };
    nimm(antwortVonAusBauherr(quellen?.quellen?.stammdaten));
    (quellen?.quellen?.kontakte || []).forEach(k => nimm(antwortVonAusKontakt(k)));
    weitere.sort(SORTIERUNG.compare);
    return { passend, weitere };
};

/** Was das Feld „Antwort von" zum gespeicherten Wert zeigt. */
export type AntwortVonModus =
    | { modus: 'leer' }
    | { modus: 'liste'; eintrag: string }
    | { modus: 'frei'; text: string };

/**
 * Der gespeicherte Wert gegen die Auswahl: leer → „nicht erfasst"; steht er
 * in der Liste (eine Zeile, ohne Gross/klein — dieselbe Gleichheit wie beim
 * Entdoppeln), ist DIESER Eintrag gewaehlt; sonst ist es eine freie Angabe
 * mit genau dem gespeicherten Text (von Hand getippt oder aus einer
 * Antwortmail). Rein abgeleitet — ein von aussen neu geladener Wert gilt
 * sofort.
 */
export const antwortVonModus = (
    wert: string | null | undefined,
    liste: readonly string[],
): AntwortVonModus => {
    const v = einzeilig(wert);
    if (!v) return { modus: 'leer' };
    const klein = v.toLowerCase();
    const eintrag = liste.find(e => einzeilig(e).toLowerCase() === klein);
    return eintrag ? { modus: 'liste', eintrag } : { modus: 'frei', text: String(wert) };
};

// ═══════════════════════════════════════════════════════════════════════════
// ABSENDER EINER EINGEFUEGTEN MAIL — nur lokal, geht nie an die KI
// ═══════════════════════════════════════════════════════════════════════════

export interface MailAbsender {
    /** Anzeigename, '' wenn die Mail nur die Adresse nennt. */
    name: string;
    /** Mailadresse, '' wenn der Kopf nur den Namen nennt (Outlook intern). */
    email: string;
}

/** Eine Mailadresse als ganzer Wert. */
const ADRESSE = /^[\p{L}\p{N}._%+'-]+@[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)+$/u;
/** „Name <adresse>", „Name [mailto:adresse]", „<adresse>". */
const NAME_UND_ADRESSE = /^(.*?)[<[]\s*(?:mailto:)?\s*([^<>[\]\s]+@[^<>[\]\s]+)\s*[>\]]\s*$/i;
/** Kopfzeile „Von:"/„From:" mit Wert. */
const VON_ZEILE = /^\s*(?:von|from)\s*:\s*(.*)$/i;
/** Weitere Kopfzeilen — belegen, dass „Von: <Name>" ein Mailkopf ist. */
const WEITERE_KOPFZEILE = /^\s*(?:an|cc|bcc|gesendet|betreff|datum|to|sent|subject|date)\s*:/i;
/** Betreff- bzw. Ordnerzeile ueber einem Gmail-Kopf („AW: …", „Posteingang",
 *  „Posteingang x" mit dem Knopf zum Entfernen des Labels). */
const VORLAUF_ZEILE = /^\s*(?:(?:aw|re|wg|fw|fwd|antw)\s*:.*|(?:posteingang|inbox)(?:\s+[x×])?)\s*$/i;
/** Freie Betreffzeile ueber einem Gmail-Kopf — Gmail zeigt den Betreff des
 *  Verlaufs, bei der Antwort auf die eigene Mail also oft OHNE AW/RE
 *  („Klärungsliste BV Musterstraße"). Eine kurze Zeile ohne Adresse; als
 *  Betreff gilt sie nur als ERSTE Zeile und nur, wenn darunter ein ganzer
 *  Gmail-Kopf steht (Absender MIT Datum oder „an mich") — siehe `gmailKopf`. */
const FREIER_BETREFF = /^[^<>@\n]{2,200}$/;
/** Gmail: die Absenderzeile ist „Name <adresse>" (ohne Doppelpunkt im Namen). */
const GMAIL_ABSENDER = /^\s*(?:"?([^"<>:@\n]*?)"?\s*)?<\s*(?:mailto:)?([^<>\s]+@[^<>\s]+)\s*>\s*$/i;
/** Gmail, auf zwei Zeilen: erst der Name, dann „<adresse>". */
const GMAIL_NUR_NAME = /^\s*[^\s:<>@][^:<>@]{0,78}$/;
const GMAIL_NUR_ADRESSE = /^\s*<\s*(?:mailto:)?([^<>\s]+@[^<>\s]+)\s*>\s*$/i;
/** Gmail-Empfaengerzeile „an mich", „an Bauleitung, mich", „to me". */
const GMAIL_EMPFAENGER = /^\s*(?:an|to)\s+(?:.{0,60}[\s,])?(?:mich|me|ich)\b[^\n]{0,4}$/i;

/** Gmail-Datumszeile: „Mo., 26. Okt., 10:15 (vor 2 Tagen)", „10:15 (vor 2
 *  Stunden)", „Mon, Oct 26, 2026, 10:15 AM". Eine Uhrzeit allein reicht
 *  nicht — „Baubeginn 7:30 Uhr" ist Text. */
const istGmailDatum = (zeile: string): boolean => {
    if (zeile.length > 80 || !/\b\d{1,2}:\d{2}\b/.test(zeile)) return false;
    return /^\s*\d{1,2}:\d{2}(?:\s*[ap]\.?m\.?)?\s*(?:\(.*\))?\s*$/i.test(zeile)
        || /\((?:vor\s.*|.*\bago)\)/i.test(zeile)
        || /^\s*(?:mo|di|mi|do|fr|sa|so|mon|tue|wed|thu|fri|sat|sun)[a-z]*\.?,/i.test(zeile)
        || /^\s*\d{1,2}\.\s?(?:jan|feb|mär|mar|apr|mai|jun|jul|aug|sep|okt|nov|dez)[a-zä]*\.?\s/i.test(zeile)
        || /^\s*(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{1,2}\b/i.test(zeile);
};

/** Zerlegt den Wert einer Absenderangabe in Name und Adresse. */
const absenderAusWert = (roh: string): MailAbsender | null => {
    const wert = einzeilig(roh);
    if (!wert) return null;
    let name = '';
    let email = '';
    const treffer = wert.match(NAME_UND_ADRESSE);
    if (treffer) {
        name = treffer[1];
        email = treffer[2];
    } else if (ADRESSE.test(wert)) {
        email = wert;
    } else {
        name = wert;
    }
    name = einzeilig(name).replace(/^["'„“”‚‘]+|["'“”‘’]+$/g, '').trim();
    email = einzeilig(email).replace(/^mailto:/i, '');
    if (email && !ADRESSE.test(email)) email = '';
    if (name && email && name.toLowerCase() === email.toLowerCase()) name = '';
    if (!name && !email) return null;
    return { name, email };
};

/** Die Absenderzeile(n) eines Gmail-Kopfes ab Zeile `i`: „Name <adresse>"
 *  auf einer, oder Name und „<adresse>" auf zwei Zeilen. Nur mit Adresse;
 *  eine Ordnerzeile („Posteingang") ist nie der Name. */
const gmailAbsenderAb = (zeilen: readonly string[], i: number): { absender: MailAbsender; zeilen: number } | null => {
    if (i >= zeilen.length || VORLAUF_ZEILE.test(zeilen[i])) return null;
    const eine = zeilen[i].match(GMAIL_ABSENDER);
    if (eine) {
        const absender = absenderAusWert(`${eine[1] || ''} <${eine[2]}>`);
        return absender && absender.email ? { absender, zeilen: 1 } : null;
    }
    const zweite = i + 1 < zeilen.length ? zeilen[i + 1].match(GMAIL_NUR_ADRESSE) : null;
    if (zweite && GMAIL_NUR_NAME.test(zeilen[i])) {
        const absender = absenderAusWert(`${zeilen[i]} <${zweite[1]}>`);
        return absender && absender.email ? { absender, zeilen: 2 } : null;
    }
    return null;
};

/**
 * Der Kopf einer aus Gmail kopierten Mail: „Name <adresse>" (oder Name und
 * „<adresse>" auf zwei Zeilen), danach Datum und „an mich". Darueber duerfen
 * Betreff- und Ordnerzeilen stehen („AW: …", „Posteingang") — als ERSTE
 * Zeile auch ein Betreff ohne AW/RE, dann aber nur mit Datum oder „an mich"
 * unter dem Absender, damit eine Textzeile mit Adresse darunter kein Kopf
 * wird. Liefert das Ende des Kopfes (Zeichenposition) und den Absender —
 * oder `null`.
 */
const gmailKopf = (mail: string): { ende: number; absender: MailAbsender } | null => {
    const zeilen = mail.split('\n');
    let i = 0;
    let pos = 0;
    const weiter = () => { pos += zeilen[i].length + 1; i += 1; };
    const leerzeilenWeg = () => { while (i < zeilen.length && zeilen[i].trim() === '') weiter(); };

    // Vor dem Absender hoechstens drei Zeilen Betreff/Ordner, Leerzeilen dazwischen.
    leerzeilenWeg();
    let freierBetreff = false;
    let vorlauf = 0;
    let gefunden = gmailAbsenderAb(zeilen, i);
    while (!gefunden && i < zeilen.length && vorlauf < 3) {
        if (!VORLAUF_ZEILE.test(zeilen[i])) {
            if (vorlauf > 0 || !FREIER_BETREFF.test(zeilen[i].trim())) return null;
            freierBetreff = true;
        }
        vorlauf += 1;
        weiter();
        leerzeilenWeg();
        gefunden = gmailAbsenderAb(zeilen, i);
    }
    if (!gefunden) return null;
    for (let n = 0; n < gefunden.zeilen; n++) weiter();

    // Datum und Empfaenger — hoechstens vier Zeilen, Leerzeilen dazwischen.
    let meta = 0;
    while (i < zeilen.length && meta < 4) {
        const z = zeilen[i];
        if (z.trim() === '') { weiter(); continue; }
        if (!istGmailDatum(z) && !GMAIL_EMPFAENGER.test(z)) break;
        meta += 1;
        weiter();
    }
    // Ein freier Betreff zaehlt nur mit Datum oder „an mich" — sonst ist die erste Zeile Text.
    if (freierBetreff && meta === 0) return null;
    // Leerzeilen vor dem Text gehoeren noch zum Kopf.
    leerzeilenWeg();
    return { ende: Math.min(pos, mail.length), absender: gefunden.absender };
};

/**
 * Der Absender einer eingefuegten Mail — aus dem Kopf GANZ OBEN:
 *   - Outlook/Thunderbird/Gmail-Original: „Von:"/„From:" mit „Name
 *     <adresse>", „"Keller, Anna" <adresse>", „Name [mailto:adresse]",
 *     nur Adresse oder (Outlook intern) nur Name — ein blosser Name gilt nur,
 *     wenn weitere Kopfzeilen (Gesendet, An, Betreff …) ihn als Kopf belegen,
 *   - Gmail aus der Ansicht kopiert: „Name <adresse>" als erste Zeile oder
 *     unter Betreff und Ordner (`gmailKopf`).
 * Ein „Von:" im zitierten Verlauf weiter unten ist meist die EIGENE Mail und
 * zaehlt nicht. `null`, wenn kein Absender erkennbar ist.
 *
 * NUR LOKAL — das Ergebnis geht nie an die KI.
 */
export const absenderAusMail = (text: string | null | undefined): MailAbsender | null => {
    const mail = String(text || '').replace(/\r\n?/g, '\n');
    const gmail = gmailKopf(mail);
    if (gmail) return gmail.absender;

    const { anfang } = eigenerTextBereich(mail);
    const kopf = mail.slice(0, anfang).split('\n');
    const i = kopf.findIndex(z => VON_ZEILE.test(z));
    if (i < 0) return null;
    // Outlook bricht lange Absender eingerueckt um — die Folgezeile gehoert dazu.
    let wert = (kopf[i].match(VON_ZEILE) || [])[1] || '';
    for (let j = i + 1; j < kopf.length && /^\s+\S/.test(kopf[j]) && !WEITERE_KOPFZEILE.test(kopf[j]); j++) {
        wert += ` ${kopf[j].trim()}`;
    }
    const absender = absenderAusWert(wert);
    if (!absender) return null;
    if (!absender.email) {
        const weitere = kopf.some((z, j) => j !== i && WEITERE_KOPFZEILE.test(z));
        if (!weitere || /^\d{1,2}[:.]\d{2}\b/.test(absender.name)) return null;
    }
    return absender;
};

/** „Name <adresse>", sonst Name oder Adresse — die ANZEIGE des Absenders im
 *  Dialog (nur lokal). Unter „Antwort von" steht `antwortVonAusAbsender`. */
export const absenderText = (a: MailAbsender | null | undefined): string => {
    if (!a) return '';
    const name = einzeilig(a.name);
    const email = einzeilig(a.email);
    if (name && email) return `${name} <${email}>`;
    return name || email;
};

/** Was aus dem Absender unter „Antwort von" steht: der Name — die Adresse
 *  nur, wenn kein Name erkennbar ist. Hoechstens `ANTWORT_VON_MAX` Zeichen;
 *  eine laengere Adresse wird nicht abgeschnitten, sondern weggelassen. */
export const antwortVonAusAbsender = (a: MailAbsender | null | undefined): string => {
    if (!a) return '';
    const name = einzeilig(a.name);
    if (name) return name.slice(0, ANTWORT_VON_MAX).trim();
    const email = einzeilig(a.email);
    return email.length <= ANTWORT_VON_MAX ? email : '';
};

/** Ersatz fuer jede Mailadresse im Text an die KI. */
export const ADRESSE_ENTFERNT = '[Adresse entfernt]';
/** Eine Mailadresse irgendwo im Text, samt „mailto:". */
const ADRESSE_IM_TEXT = /(?:mailto:)?[\p{L}\p{N}._%+-][\p{L}\p{N}._%+'-]*@[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)+/giu;

/** Jede Mailadresse im Text → „[Adresse entfernt]". */
export const ohneAdressen = (text: string): string => text.replace(ADRESSE_IM_TEXT, ADRESSE_ENTFERNT);

/** Trennlinien am Ende des eigenen Texts („____", „-----") — Outlook setzt
 *  sie vor den zitierten Verlauf; ohne den Verlauf sind sie nur Rest. */
const TRENNLINIEN_AM_ENDE = /(?:\n[ \t]*[_\-=*]{5,}[ \t]*)+\s*$/;

/**
 * Der Teil der eingefuegten Mail, der an die KI geht: NUR der eigene Text —
 * ohne den Kopf ganz oben (Von/An/Gesendet/Betreff, Betreffzeile,
 * Gmail-Absender mit Datum und „an mich", auch unter einem Betreff ohne
 * AW/RE) und ohne den zitierten Verlauf unten („Von: … Gesendet: …",
 * „-----Ursprüngliche Nachricht-----", „Am … schrieb …:"), getrimmt. Den
 * Bereich bestimmt dieselbe Regel wie in der Auswertung
 * (`eigenerTextBereich`) — was dort nie Antwort sein darf, muss gar nicht
 * erst hinaus. Zur Absicherung wird danach jede noch verbliebene Mailadresse
 * (Signatur, Text) zu „[Adresse entfernt]".
 */
export const mailTextFuerKi = (text: string | null | undefined): string => {
    const mail = String(text || '').replace(/\r\n?/g, '\n').trim();
    const gmail = gmailKopf(mail);
    const rest = gmail ? mail.slice(gmail.ende) : mail;
    const { anfang, ende } = eigenerTextBereich(rest);
    const eigener = rest.slice(anfang, ende).trimEnd();
    return ohneAdressen(`\n${eigener}`.replace(TRENNLINIEN_AM_ENDE, '')).trim();
};

// ═══════════════════════════════════════════════════════════════════════════
// QUELLE BEIM UEBERNEHMEN
// ═══════════════════════════════════════════════════════════════════════════

export type QuellenAenderung = Partial<Pick<Planungsfrage, 'antwort_von' | 'antwort_weg' | 'antwort_beleg_id'>>;

/**
 * Quelle fuer eine aus einer Mail uebernommene Antwort: `antwort_weg` =
 * E-Mail und `antwort_von` = Name des Absenders (`antwortVonAusAbsender`).
 *
 *   - Setzen und Anhaengen: beides nur, wo an der Frage noch nichts steht —
 *     vorhandene Werte bleiben.
 *   - `ersetzen` (die bisherige Antwort wird ausdruecklich durch die Mail
 *     ersetzt): die Quelle ist jetzt diese Mail. Weg E-Mail, „Antwort von"
 *     = Absender — ohne erkannten Absender LEER, damit keine falsche Person
 *     stehen bleibt —, und der Beleg wird geloest, weil der Antworttext
 *     nicht mehr aus ihm stammt.
 */
export const quelleAusMail = (
    frage: Partial<Pick<Planungsfrage, 'antwort_von' | 'antwort_weg'>>,
    absender: MailAbsender | null | undefined,
    opt: { ersetzen?: boolean } = {},
): QuellenAenderung => {
    const von = antwortVonAusAbsender(absender);
    if (opt.ersetzen) return { antwort_von: von || null, antwort_weg: 'email', antwort_beleg_id: null };
    const aenderung: QuellenAenderung = {};
    if (von && !einzeilig(frage.antwort_von)) aenderung.antwort_von = von;
    if (!antwortWegAus(frage.antwort_weg)) aenderung.antwort_weg = 'email';
    return aenderung;
};

// ═══════════════════════════════════════════════════════════════════════════
// FELD „ANTWORT VON" — gespeichert beim Verlassen, nicht je Tastendruck
// ═══════════════════════════════════════════════════════════════════════════

/** Eingabe im Feld „Antwort von" → der zu speichernde Wert: getrimmt, auf die
 *  Hoechstlaenge des CHECK gekuerzt, leer = `null`. */
export const antwortVonAusEingabe = (eingabe: string | null | undefined): string | null => {
    const wert = String(eingabe ?? '').trim().slice(0, ANTWORT_VON_MAX).trim();
    return wert || null;
};

/** Weicht die Eingabe vom gespeicherten Wert ab? Rand-Leerraum und „leer"
 *  gegen `null` zaehlen nicht — dann wird nicht gespeichert. */
export const antwortVonGeaendert = (
    eingabe: string | null | undefined,
    gespeichert: string | null | undefined,
): boolean => antwortVonAusEingabe(eingabe) !== antwortVonAusEingabe(gespeichert);
